import { getAccessToken } from '@/utils/tokenStore';
import dayjs from 'dayjs';
import {logging, jwtDecoder} from "@/utils/utils";

/**
 * contracts-api용 v2 API 모듈 (SPEC: 전자계약 v1→v2 전환, 첫 화면=목록)
 *
 * 이게 왜 있는가:
 * - 기존 화면(src/views/ContractManagement/api.js)은 inapi(v1)를 호출한다. v1은 절대 수정하지 않는다는
 *   전제 아래, 신규 contracts-api를 호출하는 별도 모듈을 새로 만들었다.
 * - 여기 포함된 함수는 v1 목록 화면(ContractList.vue)이 실제로 사용하는 것 중, contracts-api에
 *   characterization copy(요청/응답 형태 동일)로 이미 포팅된 엔드포인트만이다. 함수명/시그니처는
 *   v1 api.js와 최대한 동일하게 맞춰서 화면 코드(ContractListV2.vue)가 v1과 거의 그대로 읽히게 했다.
 * - 요청/응답 바디 형태는 v1 api.js를 그대로 베꼈다 — 새로 추정하지 않았다.
 *
 * v1은 지금도 살아있는 경로다. 이 모듈은 v1을 대체하지 않고 병행한다.
 *
 * changeSigningDue: 작업 지시 표에서 누락됐던 항목이라 한때 "미검증"으로 표시돼 있었으나,
 * 스테이징 contracts-api 실호출로 `PUT /contracts/:contractId/change-signing-due` 존재를 확인했다
 * (무토큰 401 = 라우트 존재 + 가드 동작). v1과 동일 경로·동일 페이로드다.
 */
export default function ($contractsApiInstance, $store) {
    return {
        // 목록
        getContracts: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts`, params)
                .then((response) => {
                    logging().exceptProd('contracts(v2) ==>', response.data)
                    response.data.docs = response.data.docs.map(d => {
                        return {isChecked: false, ...d}
                    })
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 목록 요약 통계 — 목록과 **같은 params** 로 호출한다(백엔드도 같은 필터로 집계).
         * 로딩 스피너를 띄우지 않는다: 목록 조회와 병렬로 나가는 보조 정보라, 여기서 스피너를
         * 켰다 껐다 하면 목록 로딩이 끝나기도 전에 화면이 깜빡인다.
         */
        getContractStats: async (params, success, fail) => {
            return $contractsApiInstance.post(`/contracts/stats`, params)
                .then((response) => success(response), (error) => fail(error));
        },

        // 상세
        getContract: async (contractId, isOriginalDocs, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.get(`/contracts/${contractId}?isOriginalDocs=${isOriginalDocs}`)
                .then((response) => {
                    logging().exceptProd('contract(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 엑셀
        fetchContractsExcel: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post('/contracts/excel', {...params, limit: 100000000}, {
                responseType: 'blob',
                headers: {}
            })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    const formattedDate = dayjs().format('YYYYMMDD');
                    link.setAttribute('download', `전자계약목록_${formattedDate}.xlsx`);
                    document.body.appendChild(link);
                    link.click();

                    window.URL.revokeObjectURL(url);
                    link.parentNode.removeChild(link);
                    success(response);
                })
                .catch(error => {
                    console.error('Error:', error);
                    fail(error);
                })
                .finally(() => {
                    $store.commit('setIsLoading', false);
                });
        },

        // 재발송
        resendRequest: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/${params}/resend-sign-request`)
                .then((response) => {
                    logging().exceptProd('resend request(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 취소
        cancelContract: (contractId, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/${contractId}/cancel`, {
                message: "서명요청 취소",
                accessibleByParticipant: false
            })
                .then((response) => {
                    logging().exceptProd('cancel(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // PDF 다운로드
        getContractsPdfDownload: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/pdf-download`, params)
                .then((response) => {
                    logging().exceptProd('getContractsPdfDownload(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 대면서명 링크
        sendSecureSignLink: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/secure-sign-link`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    logging().exceptProd(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 완료본 전달
        sendCompleteContract: (param, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/${param?.contractId}/send-complete-contract`, [
                ...param?.contactList
            ])
                .then((response) => {
                    logging().exceptProd('send complete contract(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 삭제(소프트)
        deleteContracts: async (contractIds, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.delete('/contracts', {data: {contractIds}})
                .then((response) => {
                    success(response);
                })
                .catch((error) => {
                    fail(error);
                })
                .finally(() => {
                    $store.commit('setIsLoading', false);
                });
        },

        // 거래처 동기화
        syncClient: async (params, success, fail) => {
            return $contractsApiInstance.patch(`/contracts/sync/hospital`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },

        // 부서명 목록 (거래처 도메인이지만 v1과 동일하게 companies 엔드포인트 사용 — 화면 필터에서 씀)
        getDistinctDeptName: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            try {
                const response = await $contractsApiInstance.post(`/companies/distinct/deptName`, params);

                const deptNames = response.data
                    .filter(item => item.includes("지점") || item.includes("영업소") || item === "종합병원팀")
                    .map(item => ({label: item, value: item}));

                success(deptNames);
            } catch (error) {
                console.log(error);
                fail(error);
            } finally {
                $store.commit('setIsLoading', false);
            }
        },

        // 계약 유형(템플릿) 목록 — v1 ContractTypeList.vue의 api().getContractTemplates 를
        // contracts-api로 characterization port한 것. 요청/응답 형태 동일(companyId는 v1과 동일하게
        // JWT에서 직접 뽑아 body에 얹는다). 스테이징 contracts-api 실호출로 존재 확인됨.
        getContractTemplates: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            const companyId = jwtDecoder(getAccessToken());
            return $contractsApiInstance.post(`/contracts/templates`, {...params, companyId})
                .then((response) => {
                    logging().exceptProd('contract templates(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 스테이징 실호출로 라우트 존재 확인 완료(401). v1과 동일 경로·페이로드.
        changeSigningDue: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            const {contractId, date} = params;
            return $contractsApiInstance.put(`/contracts/${contractId}/change-signing-due`, {
                date: date,
            })
                .then((response) => {
                    logging().exceptProd('signing due(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        /**
         * 아래부터는 계약 생성 화면(MulpumV6V2 등)이 쓰는 함수. v1
         * src/views/ContractManagement/api.js의 동명 함수를 characterization copy했다 —
         * 엔드포인트·요청 바디 형태는 v1과 동일, $apiInstance만 $contractsApiInstance로 교체했다.
         * SPEC 작업지시서에 이미 존재가 확인된 엔드포인트만 옮겼다(create-by-template,
         * embedded-drafts-by-template, temp, accounts/manufacturer).
         */

        // 영업담당자 계정 목록 (담당자/지점장/센터장 선택 셀렉트용)
        getManufacturerAccounts: async (success, fail) => {
            return $contractsApiInstance.get(`/accounts/manufacturer?page=1&limit=10000`)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },

        // 계약 생성 — ⚠️ 실제 계약을 만든다. 화면의 "계약서 생성" 버튼에서만 호출되어야 하며,
        // 검증 스크립트/자동화에서 절대 호출하지 않는다.
        createContract: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/create-by-template`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },

        // 계약 미리보기(임베드) 초안 생성 — 서명요청이 아니라 모두싸인 임베드 미리보기 URL만 받는다.
        createContractDraft: async (params, success, fail) => {
            return $contractsApiInstance.post(`/contracts/embedded-drafts-by-template`, params)
                .then((response) => {
                    success(response);
                }, (error) => {
                    console.log(error);
                    fail(error);
                })
        },

        // 임시저장 조회/생성 (화면 이탈 방지용 로컬 초안, 계약 생성이 아님)
        getTempContract: async (params, success, fail) => {
            const {contractType} = params
            return $contractsApiInstance.get(`/contracts/temp/${contractType}`)
                .then((response) => {
                    logging().exceptProd('get temp contract(v2) ==>', response.data)
                    success(response.data);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
        createTempContract: async (params, success, fail) => {
            $store.commit('setIsLoading', true);
            return $contractsApiInstance.post(`/contracts/temp`, params)
                .then((response) => {
                    logging().exceptProd('create temp contract(v2) ==>', response.data)
                    success(response);
                }, (error) => {
                    fail(error);
                }).finally(() => $store.commit('setIsLoading', false));
        },
    }
}
